import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { API } from '../App';
import { useAuthedImage, useAuthedImagePolling } from '../hooks/useAuthedImage';
import CameraCapture from '../components/CameraCapture';
import './FootAnalysisPage.css';
import './NewPatientPage.css';

const compressImage = (file, maxWidth = 800, quality = 0.7) =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width  = img.width  * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', quality);
    };
    img.src = URL.createObjectURL(file);
  });

// Talons et arche gauche/droite ont leur planche clinique (voir
// FootChartCard) en plus de l'overlay squelette IA. Le podoscope a lui
// aussi perdu ses boutons manuels Présent/Absent (ancienne UI) — l'isthme
// est désormais uniquement déterminé par l'auto-analyse IA
// (handleAutoAnalyze / foot-labels), sans possibilité de correction
// manuelle par bouton. Le bouton "Générer le squelette" (overlay IA)
// génère les 4 vues (talons, arche G/D, podoscope) en un seul clic.
const DEFAULT_LABELS = {
  podoscope_isthme: 'present',
};

const getViews = (t) => [
  { key: 'heels',      label: t('foot.views.heels') },
  { key: 'arch_left',  label: t('foot.views.arch_left') },
  { key: 'arch_right', label: t('foot.views.arch_right') },
  { key: 'podoscope',  label: t('foot.views.podoscope') },
];

// 3 planches, chacune avec un trait gauche (rouge) + un trait droit (bleu).
// L'image est servie par le backend (proxy Azure Blob Storage), voir
// GET /api/skeleton-ref/{filename} — mêmes fichiers que la page posturale.
// Les trois planches (talon, arche latérale, arche podoscope) sont toutes
// recadrées (plus de marge de règle) -> rulerFraction: 0 pour les trois.
const getFootChartItems = (t) => [
  { key: 'heels_varus_valgus', label: t('foot.labels.heels_chart'), img: 'scale_heels_varus_valgus.png', rulerFraction: 0 },
  { key: 'arch_shape',         label: t('foot.labels.arch_chart'),  img: 'scale_foot_arch.png',           rulerFraction: 0 },
  { key: 'pes_cavus_planus',   label: t('foot.labels.pes_chart'),   img: 'scale_pieds_creux_plats.png',   rulerFraction: 0 },
];

function toFootChartEstimateState(serverEstimates) {
  const out = {};
  Object.entries(serverEstimates || {}).forEach(([key, sides]) => {
    out[key] = {};
    Object.entries(sides || {}).forEach(([side, v]) => {
      out[key][side] = { xNormalized: v.x_normalized ?? 0.5, comment: v.comment || '', manuallyEdited: false };
    });
  });
  return out;
}

// Inverse de toFootChartEstimateState, pour sauvegarder l'état local
// (y compris les corrections manuelles) au format attendu par le backend.
function toServerFootChartEstimates(chartEstimates) {
  const out = {};
  Object.entries(chartEstimates || {}).forEach(([key, sides]) => {
    out[key] = {};
    Object.entries(sides || {}).forEach(([side, v]) => {
      out[key][side] = { x_normalized: v.xNormalized, comment: v.comment };
    });
  });
  return out;
}

export default function FootAnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const consultationId = searchParams.get('consultation_id') || '';
  const qs = consultationId ? `?consultation_id=${consultationId}` : '';

  const [patient,       setPatient]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [photos,        setPhotos]        = useState({ heels: null, arch_left: null, arch_right: null, podoscope: null });
  const [uploading,     setUploading]     = useState(false);
  const [uploadError,   setUploadError]   = useState('');
  const [photosReady,   setPhotosReady]   = useState(false);
  const [labels,        setLabels]        = useState(DEFAULT_LABELS);
  const [analyzing,     setAnalyzing]     = useState(false);
  const [chartEstimates,setChartEstimates]= useState({});
  const [chartAnalyzing,setChartAnalyzing]= useState(false);
  // Temps pris par la dernière analyse IA des planches pieds, en ms --
  // affiché à l'écran, même logique que sur AnalysisPage.js.
  const [chartDurationMs, setChartDurationMs] = useState(null);
  const [reportOpen,    setReportOpen]    = useState(false);
  const [reportText,    setReportText]    = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [chatMessages,  setChatMessages]  = useState([]);
  const [chatInput,     setChatInput]     = useState('');
  const [chatSending,   setChatSending]   = useState(false);
  // photoVersion : incrémenté à chaque remplacement de photo pour
  // "casser" le cache navigateur sur l'URL /foot-annotated/{view} (qui ne
  // change pas de nom sinon). replacingView : vue en cours de remplacement
  // (affiche un indicateur de chargement sur LA carte concernée).
  const [photoVersion,  setPhotoVersion]  = useState(0);
  const [replacingView, setReplacingView] = useState(null);
  // Overlay squelette IA (4 vues) : déclenché EXPLICITEMENT par le
  // praticien via un bouton dédié (cf. handleGenerateSkeleton plus bas),
  // plus automatiquement au moment de quitter la page. État (pas juste une
  // ref) car l'UI doit refléter "en cours" / "prêt" / "échec".
  const [skeletonRequested, setSkeletonRequested] = useState(false);
  // Durée (ms) de la dernière génération de squelette -- persistée côté
  // backend (foot_overlay_duration_ms), même logique que AnalysisPage.js.
  const [skeletonDurationMs, setSkeletonDurationMs] = useState(null);

  const VIEWS = getViews(t);
  const CHART_ITEMS = getFootChartItems(t);
  const heelsView     = VIEWS.find(v => v.key === 'heels');
  const archLeftView  = VIEWS.find(v => v.key === 'arch_left');
  const archRightView = VIEWS.find(v => v.key === 'arch_right');
  const podoscopeView = VIEWS.find(v => v.key === 'podoscope');

  // Vues réellement enregistrées : tout le rendu (cartes photo, planches,
  // squelette) s'y limite -- une vue absente n'a ni photo à afficher, ni
  // estimation IA, ni overlay à attendre. Les planches sont affichées dès
  // qu'AU MOINS UN de leurs côtés a sa photo (arche gauche/droite viennent
  // de deux clichés distincts, cf. FOOT_CHART_REFS côté backend).
  const savedFootViews = (patient?.foot_photos || []).map(p => p.view);
  const hasView = (key) => savedFootViews.includes(key);

  const fetchPatient = () => {
    // Renvoie la promesse : handleReplacePhoto doit pouvoir attendre que
    // `patient` soit à jour (ajout d'une vue absente) avant de relancer les
    // analyses. Même correctif que AnalysisPage.js.
    return fetch(`${API}/api/patients/${id}${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setPatient(data);
        // UNE vue suffit : les 4 vues sont indépendantes et le backend
        // n'évalue que les planches dont la photo existe (cf.
        // get_foot_chart_estimate / get_foot_labels). Exiger les 4 ici
        // bloquait l'écran d'upload alors qu'un seul cliché podoscope, par
        // exemple, donne déjà un bilan sur sa planche.
        const has = VIEWS.some(v => (data.foot_photos || []).some(p => p.view === v.key));
        setPhotosReady(has);
        if (data.foot_labels) setLabels({ ...DEFAULT_LABELS, ...data.foot_labels });
        const savedChartEstimates = data.foot_chart_estimates_validated || data.foot_chart_estimates;
        if (savedChartEstimates) setChartEstimates(toFootChartEstimateState(savedChartEstimates));
        // Overlay IA déjà généré pour au moins une des 4 vues (talons,
        // arche G/D, podoscope) lors d'un chargement précédent : pas besoin
        // de relancer gpt-image inutilement à la prochaine navigation.
        const anyOverlay = (data.foot_photos || []).some(p => p.foot_overlay_blob);
        if (anyOverlay) setSkeletonRequested(true);
        if (typeof data.foot_overlay_duration_ms === 'number') {
          setSkeletonDurationMs(data.foot_overlay_duration_ms);
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPatient(); }, [id, consultationId]);

  // Consultation historique demandée depuis PatientHistoryPage : lecture
  // seule, aucune analyse auto ni upload/remplacement possible.
  const readOnly = !!consultationId;

  const analysisStarted = useRef(false);
  const chartAnalysisStarted = useRef(false);
  useEffect(() => {
    if (readOnly) return;
    if (photosReady && !patient?.foot_labels && !analysisStarted.current) {
      analysisStarted.current = true;
      handleAutoAnalyze();
    }
    if (photosReady && !patient?.foot_chart_estimates && !chartAnalysisStarted.current) {
      chartAnalysisStarted.current = true;
      handleAutoAnalyzeCharts();
    }
  }, [photosReady, patient, readOnly]);

  const handlePhoto = (view, file) => {
    if (!file) return;
    setPhotos(p => ({ ...p, [view]: { file, preview: URL.createObjectURL(file) } }));
  };

  const handleUpload = async () => {
    // Au moins une vue, pas les 4 : le praticien prend les clichés qu'il juge
    // utiles (même règle que le backend depuis que les photos podologiques
    // sont optionnelles). Seules les vues fournies sont envoyées.
    const toUpload = VIEWS.filter(v => photos[v.key]);
    if (toUpload.length === 0) {
      setUploadError(t('foot.photos_at_least_one'));
      return;
    }
    setUploading(true); setUploadError('');
    try {
      for (const v of toUpload) {
        const compressed = await compressImage(photos[v.key].file);
        const fd = new FormData();
        fd.append('view', v.key);
        fd.append('file', compressed);
        const res = await fetch(`${API}/api/patients/${id}/foot-photo`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error(`Erreur upload ${v.label}`);
      }
      setLoading(true);
      fetchPatient();
    } catch (e) { setUploadError(e.message); }
    finally { setUploading(false); }
  };

  // Remplace UNE photo déjà enregistrée (talons, arche G/D ou podoscope)
  // pour une nouvelle consultation. Avant ce correctif, une fois les 4 vues
  // uploadées une première fois, il n'existait plus AUCUN moyen dans
  // l'interface de les changer : la section d'upload (!photosReady)
  // disparaît définitivement dès que les 4 photos existent, et les cartes
  // FootPhotoCard affichées ensuite étaient en lecture seule. Toutes les
  // consultations suivantes réutilisaient donc silencieusement les toutes
  // premières photos du patient.
  const handleReplacePhoto = async (view, file) => {
    if (!file) return;
    setReplacingView(view);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append('view', view);
      fd.append('file', compressed);
      const res = await fetch(`${API}/api/patients/${id}/foot-photo`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      setPhotoVersion(v => v + 1);
      // Recharge le patient : sert aussi bien au remplacement qu'à l'AJOUT
      // d'une vue non photographiée (les photos étant optionnelles) -- sans
      // ça la nouvelle vue n'apparaîtrait qu'après un rechargement manuel.
      await fetchPatient();
      // Les 4 vues (talons/arche G/arche D/podoscope) peuvent avoir un
      // overlay squelette désormais -- une photo remplacée invalide le
      // squelette déjà généré (calculé sur l'ancienne photo), on autorise
      // donc une nouvelle génération. Après fetchPatient, qui le remet à true
      // s'il existe un ancien overlay.
      setSkeletonRequested(false);
      // Les estimations IA existantes (planches + isthme) ont été calculées
      // sur l'ANCIENNE photo : on relance l'analyse pour rester cohérent
      // avec ce qui est maintenant affiché.
      await Promise.all([handleAutoAnalyzeCharts(), handleAutoAnalyze()]);
    } catch (e) {
      alert(t('foot.replace_error'));
    } finally {
      setReplacingView(null);
    }
  };

  // Classification texte (podoscope_isthme, + heel/arch conservés en interne
  // pour les rapports existants) — inchangé.
  const handleAutoAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/patients/${id}/foot-labels`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLabels({ ...DEFAULT_LABELS, ...data.labels });
    } catch (e) { console.error('Auto-analyse pieds échouée', e); }
    finally { setAnalyzing(false); }
  };

  // Nouvelle estimation par planche (talons + arche), gauche/droite.
  const handleAutoAnalyzeCharts = async () => {
    setChartAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/patients/${id}/foot-chart-estimate`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setChartEstimates(toFootChartEstimateState(data.estimates));
      if (typeof data.duration_ms === 'number') setChartDurationMs(data.duration_ms);
    } catch (e) { console.error('Auto-analyse planches pieds échouée', e); }
    finally { setChartAnalyzing(false); }
  };

  const formatDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

  // Même logique de sauvegarde debounced que la page posturale (cf.
  // AnalysisPage.js) : une correction manuelle non suivie d'un clic sur
  // "Générer le rapport" doit quand même être prise en compte par le bilan
  // clinique final, pas seulement rester dans le navigateur.
  const saveChartTimer = useRef(null);
  const persistChartEstimates = (nextEstimates) => {
    if (saveChartTimer.current) clearTimeout(saveChartTimer.current);
    saveChartTimer.current = setTimeout(() => {
      fetch(`${API}/api/patients/${id}/foot-chart-estimates/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estimates: toServerFootChartEstimates(nextEstimates) }),
      }).catch(() => {});
    }, 600);
  };

  const handleDragChartEstimate = (key, side, xNormalized) => {
    setChartEstimates(prev => {
      const next = {
        ...prev,
        [key]: {
          ...prev[key],
          [side]: { xNormalized, comment: t('foot.manual_correction'), manuallyEdited: true },
        },
      };
      persistChartEstimates(next);
      return next;
    });
  };

  // Lance la génération de l'overlay IA pour les 4 vues (talons, arche G/D,
  // podoscope) : déclenchée explicitement par un clic (bouton ci-dessous
  // dans le rendu), plus automatiquement en quittant la page.
  // _run_foot_overlay_generation côté backend lit foot_labels directement
  // en base, donc aucun payload à envoyer ici. Le POST met juste la tâche
  // en file (réponse quasi immédiate) ; l'avancement réel est suivi ici via
  // sondage (heelsOverlay / archLeftOverlay / ... plus bas).
  const handleGenerateSkeleton = async () => {
    if (readOnly || skeletonRequested) return;
    setSkeletonRequested(true);
    try {
      const res = await fetch(`${API}/api/patients/${id}/foot-overlay`, { method: 'POST' });
      if (!res.ok) throw new Error();
    } catch (e) {
      setSkeletonRequested(false);
      alert(t('foot.skeleton_trigger_error'));
    }
  };

  const handleGoToGaitAnalysis = () => {
    navigate(`/gait/${id}${qs}`);
  };

  // Suivi de la génération en cours (ou déjà terminée), vue par vue. URL null
  // pour une vue non photographiée : aucun overlay ne sera jamais généré pour
  // elle, la sonder laisserait le squelette éternellement "en cours".
  const footOverlayUrl = (view) =>
    skeletonRequested && hasView(view) ? `${API}/api/patients/${id}/foot-overlay/${view}${qs}` : null;
  const heelsOverlay     = useAuthedImagePolling(footOverlayUrl('heels'),      { intervalMs: 4000, maxAttempts: 90 });
  const archLeftOverlay  = useAuthedImagePolling(footOverlayUrl('arch_left'),  { intervalMs: 4000, maxAttempts: 90 });
  const archRightOverlay = useAuthedImagePolling(footOverlayUrl('arch_right'), { intervalMs: 4000, maxAttempts: 90 });
  const podoscopeOverlay = useAuthedImagePolling(footOverlayUrl('podoscope'),  { intervalMs: 4000, maxAttempts: 90 });
  const overlayByView = {
    heels: heelsOverlay, arch_left: archLeftOverlay,
    arch_right: archRightOverlay, podoscope: podoscopeOverlay,
  };
  const footOverlays = VIEWS.filter(v => hasView(v.key)).map(v => overlayByView[v.key]);
  const skeletonDone   = skeletonRequested && footOverlays.length > 0
    && footOverlays.every(o => o.status === 'loaded');
  const skeletonFailed = skeletonRequested && footOverlays.some(o => o.status === 'failed');

  // Dès que le squelette est prêt, on récupère la durée réelle mesurée côté
  // backend (foot_overlay_duration_ms) plutôt que de chronométrer côté
  // client -- même logique que AnalysisPage.js.
  useEffect(() => {
    if (!skeletonDone) return;
    fetch(`${API}/api/patients/${id}${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (typeof d.foot_overlay_duration_ms === 'number') setSkeletonDurationMs(d.foot_overlay_duration_ms);
      })
      .catch(() => {});
  }, [skeletonDone]);

  // ── Rapport podologique + chat ──────────────────────────────────────────
  // Construit le résumé clinique envoyé à l'agent IA, entièrement dans la
  // langue de l'interface (même logique que AnalysisPage.js).
  const buildFootAssessmentSummary = () => {
    const lines = CHART_ITEMS.map(item => {
      const est = chartEstimates[item.key];
      const sideLine = (side, sideLabel) => {
        const s = est?.[side];
        if (!s) return `  - ${t('foot.not_evaluated', { label: sideLabel })}`;
        const pct = Math.round(s.xNormalized * 100);
        const origin = s.manuallyEdited ? t('foot.origin_manual') : t('foot.origin_ai');
        const note = s.comment ? ` ${t('foot.note_label', { comment: s.comment })}` : '';
        return `  - ${t('foot.position_scale', { label: sideLabel, pct })} (${origin}).${note}`;
      };
      return `- ${item.label} :\n${sideLine('left', t('foot.left'))}\n${sideLine('right', t('foot.right'))}`;
    });
    // Sans photo podoscope, l'isthme n'a pas été évalué : il ne faut PAS
    // reprendre la valeur par défaut de DEFAULT_LABELS ('present'), qui
    // enverrait au rapport une constatation clinique sans image derrière.
    const isthme = hasView('podoscope') ? labels.podoscope_isthme : null;
    const isthmeValue = isthme === 'present'
      ? t('foot.options.present')
      : isthme === 'absent'
        ? t('foot.options.absent')
        : t('foot.unassessed');
    lines.push(t('foot.podoscope_summary', { value: isthmeValue }));
    return lines.join('\n');
  };

  const handleOpenReport = async () => {
    setReportOpen(true);
    setReportLoading(true);
    try {
      const summary = buildFootAssessmentSummary();
      const messages = [{ role: 'user', content: `${t('foot.report_intro')}\n${summary}` }];
      const lang = (i18n.language || 'fr').split('-')[0];
      const res = await fetch(`${API}/api/foot-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: id, messages, lang }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setReportText(data.report);
      setChatMessages([
        { role: 'user', content: messages[0].content },
        { role: 'assistant', content: data.report },
      ]);
    } catch (e) {
      setReportText(t('foot.report_error'));
    } finally {
      setReportLoading(false);
    }
  };

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    // Max 5 échanges par consultation (messages 0-1 = demande + rapport ;
    // les questions du praticien commencent à l'index 2). Garde-fou en plus
    // du champ désactivé côté FootReportModal, et de la limite backend.
    if (chatMessages.slice(2).filter(m => m.role === 'user').length >= 5) return;
    setChatInput('');
    const nextMessages = [...chatMessages, { role: 'user', content: text }];
    setChatMessages(nextMessages);
    setChatSending(true);
    try {
      const lang = (i18n.language || 'fr').split('-')[0];
      const res = await fetch(`${API}/api/foot-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: id, messages: nextMessages, lang }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setChatMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setChatMessages(m => [...m, { role: 'assistant', content: t('foot.chat_error') }]);
    } finally {
      setChatSending(false);
    }
  };

  if (loading) return <FootSkeleton />;
  if (!patient) return null;

  const fullName = `${patient.first_name} ${patient.last_name.toUpperCase()}`;

  return (
    <div className="foot-analysis-page">
      <header className="fa-header">
        <button className="back-btn" onClick={() => navigate(`/analysis/${id}${qs}`)}>{t('foot.back_to_postural')}</button>
        <div className="fa-title-row">
          <div>
            <p className="fa-eyebrow">{t('foot.eyebrow')}</p>
            <h1 className="fa-title">{fullName}</h1>
          </div>
        </div>
      </header>

      {readOnly && (
        <div className="historical-banner">{t('foot.readonly_banner')}</div>
      )}

      {!photosReady && !readOnly && (
        <section className="upload-section">
          <p className="upload-title">{t('foot.photos_required_title')}</p>
          <p className="upload-hint">{t('foot.photos_optional_hint')}</p>
          <div className="photos-upload-grid">
            {VIEWS.map(v => (
              <PhotoUploadSlot key={v.key} view={v} t={t}
                value={photos[v.key]} onChange={file => handlePhoto(v.key, file)} />
            ))}
          </div>
          {uploadError && <div className="np-error">{uploadError}</div>}
          <div className="upload-actions">
            <button className="btn-primary" onClick={handleUpload}
              disabled={uploading || VIEWS.every(v => !photos[v.key])}>
              {uploading ? t('foot.segmenting') : t('foot.analyze_btn')}
            </button>
          </div>
        </section>
      )}

      {!photosReady && readOnly && (
        <section className="upload-section">
          <p className="upload-title">{t('foot.no_data_historical')}</p>
        </section>
      )}

      {photosReady && (
        <section className="foot-photos-section">
          {analyzing && (
            <div className="foot-analyzing-banner">
              <div className="scan-bar" />
              <span className="dot-pulse" />
              <span>{t('foot.ai_analyzing')}</span>
            </div>
          )}
          {chartAnalyzing && (
            <div className="foot-analyzing-banner">
              <div className="scan-bar" />
              <span className="dot-pulse" />
              <span>{t('foot.ai_analyzing_charts')}</span>
            </div>
          )}
          {!chartAnalyzing && chartDurationMs !== null && (
            <div className="ai-timing-note">
              {t('foot.ai_duration', { time: formatDuration(chartDurationMs) })}
            </div>
          )}

          {/* Talons : 1 photo + planche varus/valgus (2 traits). Vue absente :
              carte "non photographiée" à la place, et pas de planche (aucune
              estimation IA n'existe pour elle). */}
          {hasView('heels') ? (
            <div className="foot-row">
              <FootPhotoCard patientId={id} view={heelsView} index={0} t={t} qs={qs}
                photoVersion={photoVersion} replacing={replacingView === 'heels'}
                onReplace={readOnly ? null : file => handleReplacePhoto('heels', file)} />
              <FootChartCard item={CHART_ITEMS[0]} value={chartEstimates['heels_varus_valgus']} readOnly={readOnly}
                onDrag={(side, xNorm) => handleDragChartEstimate('heels_varus_valgus', side, xNorm)} t={t} />
            </div>
          ) : (
            <MissingFootViewRow view={heelsView} t={t} adding={replacingView === 'heels'}
              onAdd={readOnly ? null : file => handleReplacePhoto('heels', file)} />
          )}

          {/* Arche : 2 photos (gauche/droite) + 1 seule planche creux/plat (2
              traits). Chaque côté de la planche vient de SA photo : la planche
              s'affiche dès qu'au moins un des deux clichés existe, et le trait
              du côté manquant reste simplement absent. */}
          <div className="foot-row foot-row-arch">
            <div className="foot-arch-photos">
              {hasView('arch_left') ? (
                <FootPhotoCard patientId={id} view={archLeftView} index={1} t={t} qs={qs}
                  photoVersion={photoVersion} replacing={replacingView === 'arch_left'}
                  onReplace={readOnly ? null : file => handleReplacePhoto('arch_left', file)} />
              ) : (
                <MissingFootViewRow view={archLeftView} t={t} adding={replacingView === 'arch_left'}
                  onAdd={readOnly ? null : file => handleReplacePhoto('arch_left', file)} />
              )}
              {hasView('arch_right') ? (
                <FootPhotoCard patientId={id} view={archRightView} index={2} t={t} qs={qs}
                  photoVersion={photoVersion} replacing={replacingView === 'arch_right'}
                  onReplace={readOnly ? null : file => handleReplacePhoto('arch_right', file)} />
              ) : (
                <MissingFootViewRow view={archRightView} t={t} adding={replacingView === 'arch_right'}
                  onAdd={readOnly ? null : file => handleReplacePhoto('arch_right', file)} />
              )}
            </div>
            {(hasView('arch_left') || hasView('arch_right')) && (
              <FootChartCard item={CHART_ITEMS[1]} value={chartEstimates['arch_shape']} readOnly={readOnly}
                onDrag={(side, xNorm) => handleDragChartEstimate('arch_shape', side, xNorm)} t={t} />
            )}
          </div>

          {/* Podoscope : photo + planche Pes Cavus/Pes Planus (2 traits, même
              principe que talons/arche). L'isthme n'a plus de boutons manuels
              (ancienne UI retirée) : il reste déterminé par l'auto-analyse IA
              seule (labels.podoscope_isthme). */}
          {hasView('podoscope') ? (
            <div className="foot-row foot-row-podoscope">
              <FootPhotoCard patientId={id} view={podoscopeView} index={3} t={t} qs={qs}
                photoVersion={photoVersion} replacing={replacingView === 'podoscope'}
                onReplace={readOnly ? null : file => handleReplacePhoto('podoscope', file)} />
              <FootChartCard item={CHART_ITEMS[2]} value={chartEstimates['pes_cavus_planus']} readOnly={readOnly}
                onDrag={(side, xNorm) => handleDragChartEstimate('pes_cavus_planus', side, xNorm)} t={t} />
            </div>
          ) : (
            <MissingFootViewRow view={podoscopeView} t={t} adding={replacingView === 'podoscope'}
              onAdd={readOnly ? null : file => handleReplacePhoto('podoscope', file)} />
          )}

          {!readOnly && (
            <div className="skeleton-gen-panel">
              <div className="skeleton-gen-header">
                <span className="skeleton-gen-title">{t('foot.skeleton_title')}</span>
                {!skeletonRequested && (
                  <button className="btn-secondary" onClick={handleGenerateSkeleton}>
                    {t('foot.skeleton_generate_btn')}
                  </button>
                )}
                {skeletonRequested && !skeletonDone && !skeletonFailed && (
                  <span className="skeleton-gen-status generating">
                    <span className="dot-pulse" /> {t('foot.skeleton_generating')}
                  </span>
                )}
                {skeletonDone && (
                  <span className="skeleton-gen-status done">
                    ✓ {skeletonDurationMs != null
                      ? t('foot.skeleton_done_duration', { time: formatDuration(skeletonDurationMs) })
                      : t('foot.skeleton_done')}
                  </span>
                )}
                {skeletonFailed && (
                  <span className="skeleton-gen-status failed">{t('foot.skeleton_failed')}</span>
                )}
              </div>
              {skeletonRequested && (
                <div className="skeleton-gen-thumbs">
                  {VIEWS.filter(v => hasView(v.key)).map(v => (
                    <SkeletonThumb key={v.key} label={v.label}
                      status={overlayByView[v.key].status} blobUrl={overlayByView[v.key].blobUrl} />
                  ))}
                </div>
              )}
            </div>
          )}

          {!readOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn-primary generate-btn" onClick={handleOpenReport} disabled={chartAnalyzing}>
                {t('foot.report_btn')}
              </button>
            </div>
          )}
        </section>
      )}

      {photosReady && (
        <div className="gait-analysis-btn-wrap">
          <button className="btn-primary" onClick={handleGoToGaitAnalysis}>
            {t('foot.gait_analysis_btn')}
          </button>
        </div>
      )}

      {reportOpen && (
        <FootReportModal
          t={t}
          loading={reportLoading}
          reportText={reportText}
          messages={chatMessages}
          chatInput={chatInput}
          onChatInputChange={setChatInput}
          onSend={handleSendChat}
          sending={chatSending}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}

function PhotoUploadSlot({ view, value, onChange, t }) {
  const inputRef = useRef();
  // Deux entrées pour une même vue : fichier ou caméra en direct (cf.
  // CameraCapture) -- plus rapide en consultation qu'un transfert depuis le
  // téléphone. Même structure que PosturalUploadSlot.
  const [camOpen, setCamOpen] = useState(false);
  return (
    <div className={`upload-slot ${value ? 'has-photo' : ''}`}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => onChange(e.target.files[0])} />
      {value ? (
        // Tout l'aperçu reste cliquable pour rouvrir le sélecteur de fichier
        // (comportement d'origine) ; seul le bouton caméra arrête la
        // propagation, sinon il déclencherait aussi le sélecteur.
        <div className="slot-preview" onClick={() => inputRef.current.click()}>
          <img src={value.preview} alt={view.label} />
          <div className="slot-overlay"><span>{t('foot.upload_change')}</span></div>
          <div className="slot-check">✓</div>
          <button type="button" className="live-btn slot-live-btn"
            onClick={e => { e.stopPropagation(); setCamOpen(true); }}>
            {t('camera.live_btn')}
          </button>
        </div>
      ) : (
        <div className="slot-empty">
          <span className="slot-label">{view.label}</span>
          <div className="slot-inputs">
            <button type="button" className="slot-cta-btn" onClick={() => inputRef.current.click()}>
              {t('foot.upload_choose')}
            </button>
            <button type="button" className="live-btn" onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </div>
        </div>
      )}
      <div className="slot-footer">
        <span className={`slot-status ${value ? 'ok' : 'pending'}`}>
          {value ? t('foot.slot_ready') : t('foot.slot_pending')}
        </span>
        <span>{view.label.toUpperCase()}</span>
      </div>
      {camOpen && (
        <CameraCapture label={view.label} onClose={() => setCamOpen(false)}
          onCapture={file => onChange(file)} />
      )}
    </div>
  );
}

// Vue podologique non photographiée : les photos étant optionnelles, l'écran
// doit le dire explicitement (sinon on ne distingue pas "pas photographié" de
// "erreur de chargement") et permettre d'ajouter la vue sans avoir à ouvrir
// une nouvelle consultation. Pendant de MissingViewRow dans AnalysisPage.js.
function MissingFootViewRow({ view, t, adding, onAdd }) {
  const inputRef = useRef();
  const [camOpen, setCamOpen] = useState(false);
  return (
    <div className="foot-row foot-row--missing">
      <div className="missing-view-card">
        <span className="missing-view-title">{t('foot.view_not_taken')} — {view.label}</span>
        <span className="missing-view-hint">{t('foot.view_not_taken_hint')}</span>
        {onAdd && (adding ? (
          <span className="missing-view-adding"><span className="dot-pulse" /> {t('foot.segmenting')}</span>
        ) : (
          <div className="slot-inputs">
            <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { onAdd(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="slot-cta-btn" onClick={() => inputRef.current.click()}>
              {t('foot.add_photo')}
            </button>
            <button type="button" className="live-btn" onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </div>
        ))}
      </div>
      {camOpen && (
        <CameraCapture label={view.label} onClose={() => setCamOpen(false)}
          onCapture={file => onAdd(file)} />
      )}
    </div>
  );
}

function FootPhotoCard({ patientId, view, index, photoVersion, replacing, onReplace, t, qs }) {
  const inputRef = useRef();
  const [camOpen, setCamOpen] = useState(false);

  const cidSuffix = qs ? '&' + qs.slice(1) : '';
  const photoUrl  = `${API}/api/patients/${patientId}/foot-annotated/${view.key}?v=${photoVersion ?? 0}${cidSuffix}`;
  // <img src=...> ne peut pas envoyer le jeton d'authentification -- fetch()
  // (intercepté globalement) + URL objet locale à la place. Réagit déjà à
  // photoVersion via l'URL, plus besoin d'un useEffect séparé.
  const { status: photoStatus, blobUrl } = useAuthedImage(photoUrl);

  return (
    <div className="foot-photo-card" style={{ animationDelay: `${index * 0.1}s` }}>
      <div className="foot-photo-wrap">
        {(photoStatus === 'loading' || replacing) && (
          <div className="photo-loading">
            <div className="scan-bar" />
            <div className="loading-pill"><span className="dot-pulse" /> {replacing ? t('foot.replacing_photo') : t('foot.segmenting_photo')}</div>
          </div>
        )}

        {blobUrl && (
          <img
            src={blobUrl}
            alt={view.label}
            className="foot-photo-img"
            style={{ opacity: photoStatus === 'loaded' && !replacing ? 1 : 0 }}
          />
        )}

        {/* Remplacement de photo (nouvelle consultation) : sans ce bouton,
            aucun moyen dans l'UI de changer une photo déjà enregistrée. */}
        {onReplace && !replacing && (
          <>
            <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { onReplace(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="foot-photo-change-btn"
              onClick={() => inputRef.current.click()}>
              {t('foot.change_photo')}
            </button>
            {/* Reprise directe à la caméra, sans passer par un fichier. */}
            <button type="button" className="live-btn photo-live-btn"
              onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </>
        )}
      </div>
      {camOpen && (
        <CameraCapture label={view.label} onClose={() => setCamOpen(false)}
          onCapture={file => onReplace(file)} />
      )}
      <div className="photo-foot">
        <span className="view-lbl">{view.label}</span>
        <span className={`status-pill ${photoStatus}`}>
          {photoStatus === 'loading' && t('foot.status_analyzing')}
          {photoStatus === 'loaded'  && t('foot.status_segmented')}
          {photoStatus === 'error'   && t('foot.status_error')}
        </span>
      </div>
    </div>
  );
}

// Miniature affichée dans le panneau de génération du squelette : barre de
// progression animée tant que l'image n'est pas prête, remplacée par
// l'image dès qu'elle l'est. Identique à AnalysisPage.js (postural).
function SkeletonThumb({ label, status, blobUrl }) {
  return (
    <div className={`skeleton-thumb skeleton-thumb--${status}`}>
      <div className="skeleton-thumb-body">
        {status === 'loaded' && blobUrl ? (
          <img src={blobUrl} alt={label} />
        ) : (
          <div className="skeleton-thumb-progress">
            <div className="skeleton-thumb-bar" />
          </div>
        )}
      </div>
      <span className="skeleton-thumb-label">{label}</span>
    </div>
  );
}

// Planche clinique avec DEUX traits indépendants (rouge = gauche, bleu =
// droit). Toute la planche est cliquable/glissable ; le clic saisit le
// trait le plus proche du point cliqué (comme un slider à 2 poignées),
// évitement d'avoir 2 zones de clic à viser séparément.
function FootChartCard({ item, value, onDrag, t, readOnly }) {
  const wrapRef = useRef(null);
  const [draggingSide, setDraggingSide] = useState(null);
  // Chaque planche a sa propre marge de règle (cf getFootChartItems).
  const rulerFraction = item.rulerFraction ?? 0.07;

  const fracFor = (v) => {
    if (!v) return null;
    const clamped = Math.max(0, Math.min(1, v.xNormalized));
    return rulerFraction + clamped * (1 - rulerFraction);
  };
  const leftFrac  = fracFor(value?.left);
  const rightFrac = fracFor(value?.right);

  const moveTo = (clientX, side) => {
    if (readOnly) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xNorm = Math.max(0, Math.min(1, (frac - rulerFraction) / (1 - rulerFraction)));
    onDrag(side, xNorm);
  };

  useEffect(() => {
    if (!draggingSide) return;
    const onMove = (e) => moveTo(e.touches ? e.touches[0].clientX : e.clientX, draggingSide);
    const onUp = () => setDraggingSide(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingSide]);

  const startDrag = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const clickFrac = (clientX - rect.left) / rect.width;
    let side;
    if (leftFrac === null && rightFrac === null) side = 'left';
    else if (leftFrac === null) side = 'right';
    else if (rightFrac === null) side = 'left';
    else side = Math.abs(clickFrac - leftFrac) <= Math.abs(clickFrac - rightFrac) ? 'left' : 'right';
    moveTo(clientX, side);
    setDraggingSide(side);
  };

  return (
    <div className="foot-chart-card">
      <div className="foot-chart-img-wrap" ref={wrapRef} onMouseDown={startDrag} onTouchStart={startDrag}>
        <img src={`${API}/api/skeleton-ref/${item.img}`} alt={item.label} draggable={false} />
        {item.key === 'heels_varus_valgus' && (
          <>
            <span className="foot-chart-end-label foot-chart-end-label-left">{t('foot.options.varus')}</span>
            <span className="foot-chart-end-label foot-chart-end-label-right">{t('foot.options.valgus')}</span>
          </>
        )}
        {item.key === 'pes_cavus_planus' && (
          <>
            <span className="foot-chart-end-label foot-chart-end-label-left">{t('foot.options.pes_cavus')}</span>
            <span className="foot-chart-end-label foot-chart-end-label-right">{t('foot.options.pes_planus')}</span>
          </>
        )}
        {leftFrac !== null && (
          <div className={`foot-estimate-line foot-estimate-line-left ${draggingSide === 'left' ? 'dragging' : ''}`}
            style={{ left: `${leftFrac * 100}%` }} />
        )}
        {rightFrac !== null && (
          <div className={`foot-estimate-line foot-estimate-line-right ${draggingSide === 'right' ? 'dragging' : ''}`}
            style={{ left: `${rightFrac * 100}%` }} />
        )}
      </div>
      <div className="foot-chart-label">{item.label}</div>
      <div className="foot-chart-hint">{t('foot.drag_hint')}</div>
      <div className="foot-chart-legend">
        <span className="fc-dot fc-dot-left" />
        {t('foot.left')}{value?.left ? ` — ${value.left.manuallyEdited ? '✋ ' : ''}${value.left.comment}` : ''}
      </div>
      <div className="foot-chart-legend">
        <span className="fc-dot fc-dot-right" />
        {t('foot.right')}{value?.right ? ` — ${value.right.manuallyEdited ? '✋ ' : ''}${value.right.comment}` : ''}
      </div>
    </div>
  );
}

// Modale "Rapport podologique" + chat de suivi (mêmes composants/style que
// la page posturale, mais branchés sur /api/foot-report et /api/foot-chat,
// déjà existants côté backend spécifiquement pour les pieds).
function FootReportModal({ t, loading, reportText, messages, chatInput, onChatInputChange, onSend, sending, onClose }) {
  const questionsUsed = messages.slice(2).filter(m => m.role === 'user').length;
  const limitReached = questionsUsed >= 5;
  return (
    <div className="report-overlay" onClick={onClose}>
      <div className="report-modal" onClick={e => e.stopPropagation()}>
        <div className="report-modal-header">
          <div>
            <div className="report-modal-title">{t('foot.report_title')}</div>
            <div className="report-modal-sub">{t('foot.report_subtitle')}</div>
          </div>
          <button className="btn-ghost small" onClick={onClose}>✕</button>
        </div>
        <div className="report-content">
          {loading ? (
            <div className="loading-pill"><span className="dot-pulse" /> {t('foot.report_generating')}</div>
          ) : (
            <ReactMarkdown>{reportText}</ReactMarkdown>
          )}
          {!loading && messages.length > 0 && (
            <div className="chat-messages" style={{ marginTop: 20 }}>
              {messages.slice(2).map((m, i) => (
                <div key={i} className={`chat-msg chat-${m.role}`}>{m.content}</div>
              ))}
            </div>
          )}
        </div>
        {!loading && !limitReached && (
          <div className="chat-input-row" style={{ padding: '16px 24px' }}>
            <input
              type="text"
              value={chatInput}
              onChange={e => onChatInputChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onSend(); }}
              placeholder={t('foot.chat_placeholder')}
            />
            <button className="btn-primary small" onClick={onSend} disabled={sending}>
              {sending ? '…' : t('foot.chat_send')}
            </button>
          </div>
        )}
        {!loading && limitReached && (
          <div className="chat-limit-note" style={{ padding: '16px 24px', color: 'var(--text-dim)', fontSize: 12, textAlign: 'center' }}>
            {t('foot.chat_limit', 'Limite de 5 échanges atteinte pour cette consultation.')}
          </div>
        )}
      </div>
    </div>
  );
}

function FootSkeleton() {
  return (
    <div className="foot-analysis-page">
      <div className="fa-header" style={{ marginBottom: 32 }}>
        <div className="sk sk-line" style={{ width: 80, height: 12, marginBottom: 16 }} />
        <div className="sk sk-line" style={{ width: 240, height: 32 }} />
      </div>
      {[0,1,2].map(i => (
        <div key={i} className="foot-row" style={{ marginBottom: 24 }}>
          <div className="sk" style={{ flex: 1, height: 300, borderRadius: 12 }} />
          <div className="sk" style={{ width: 280, height: 300, borderRadius: 12 }} />
        </div>
      ))}
    </div>
  );
}